// src/pages/documentation/welcome/DocumentationWelcome.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRocket,
    faUsers,
    faStore,
    faChartLine,
    faGift,
    faQrcode,
    faCheckCircle,
    faArrowRight,
    faBook,
    faHandshake,
    faEnvelope,
    faCog,
    faBullhorn,
    faFileAlt,
    faPoll,
    faUser,
    faTags,
    faBox,
    faChartBar
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const DocumentationWelcome = () => {
    const benefits = [
        {
            icon: faCog,
            title: 'Gestión Completa',
            description: 'Gestiona marcas, tiendas, clientes, productos e inventario desde un solo lugar. Importa ventas y analiza datos fácilmente',
            color: 'blue',
            link: '/documentation/gestion'
        },
        {
            icon: faBullhorn,
            title: 'Marketing Avanzado',
            description: 'Crea ofertas promocionales, recupera clientes perdidos con WinBack, envía campañas de email marketing y genera infografías',
            color: 'purple',
            link: '/documentation/marketing'
        },
        {
            icon: faChartLine,
            title: 'Analytics y Reportes',
            description: 'Analiza el rendimiento con dashboards interactivos, estadísticas detalladas y reportes ejecutivos profesionales',
            color: 'green',
            link: '/documentation/analytics'
        },
        {
            icon: faUsers,
            title: 'Fidelización Inteligente',
            description: 'Programa de puntos automático, recompensas personalizadas, segmentación RFM y recuperación de clientes inactivos',
            color: 'orange',
            link: '/documentation/marketing/recompensas'
        },
        {
            icon: faPoll,
            title: 'Feedback y Alertas',
            description: 'Recopila opiniones con encuestas, recibe alertas automáticas de eventos importantes y mantente informado',
            color: 'pink',
            link: '/documentation/feedback'
        },
        {
            icon: faUser,
            title: 'Equipos Multirol',
            description: 'Asigna trabajadores y franquiciados con permisos específicos. Cada tipo de usuario tiene acceso a lo que necesita',
            color: 'indigo',
            link: '/documentation/usuarios'
        }
    ];

    return (
        <DocumentationLayout>
            <div className="p-8 md:p-12">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-6 shadow-lg">
                        <FontAwesomeIcon icon={faStore} className="text-white text-3xl" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        Bienvenido a PetitPrime
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
                        La plataforma integral que te ayuda a gestionar, fidelizar y hacer crecer tu negocio de forma sencilla
                    </p>
                    <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                        Si eres dueño de un negocio en <strong>España, Perú o Chile</strong>, esta guía te ayudará a sacar el máximo provecho de PetitPrime, paso a paso y sin complicaciones técnicas.
                    </p>
                </div>

                {/* What is PetitPrime */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-8 mb-12">
                    <div className="flex items-start gap-4 mb-6">
                        <div className="p-3 bg-blue-600 rounded-lg">
                            <FontAwesomeIcon icon={faHandshake} className="text-white text-xl" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                ¿Qué es PetitPrime?
                            </h2>
                            <p className="text-gray-700 text-lg leading-relaxed">
                                PetitPrime es tu <strong>plataforma integral de gestión de negocio</strong>. No solo es un sistema de fidelización con puntos y recompensas, sino una solución completa que incluye gestión de clientes, productos y ventas; herramientas avanzadas de marketing; análisis y reportes detallados; y mucho más. Todo diseñado para que puedas gestionar y hacer crecer tu negocio desde un solo lugar.
                            </p>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6 mt-6">
                        <div className="bg-white rounded-lg p-6 border border-blue-100">
                            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                                Para tu negocio
                            </h3>
                            <ul className="space-y-2 text-gray-700">
                                <li>• Gestiona marcas, tiendas, clientes y productos</li>
                                <li>• Programa de puntos y recompensas automático</li>
                                <li>• Marketing avanzado (ofertas, email, WinBack)</li>
                                <li>• Analytics y reportes ejecutivos</li>
                                <li>• Recopila feedback con encuestas</li>
                                <li>• Recibe alertas automáticas importantes</li>
                                <li>• Asigna trabajadores y franquiciados</li>
                            </ul>
                        </div>
                        <div className="bg-white rounded-lg p-6 border border-blue-100">
                            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                                Para tus clientes
                            </h3>
                            <ul className="space-y-2 text-gray-700">
                                <li>• Acumulan puntos automáticamente en cada compra</li>
                                <li>• Reciben su tarjeta digital por email o WhatsApp</li>
                                <li>• Canjean puntos por premios y descuentos</li>
                                <li>• Reciben ofertas personalizadas</li>
                                <li>• Todo desde su teléfono móvil</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Benefits Grid */}
                <div className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        ¿Qué puedes hacer con PetitPrime?
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {benefits.map((benefit, index) => (
                            <Link
                                key={index}
                                to={benefit.link}
                                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer group"
                            >
                                <div className={`inline-flex p-3 rounded-lg mb-4 ${
                                    benefit.color === 'blue' ? 'bg-blue-100' :
                                    benefit.color === 'green' ? 'bg-green-100' :
                                    benefit.color === 'purple' ? 'bg-purple-100' :
                                    benefit.color === 'orange' ? 'bg-orange-100' :
                                    benefit.color === 'pink' ? 'bg-pink-100' :
                                    'bg-indigo-100'
                                }`}>
                                    <FontAwesomeIcon
                                        icon={benefit.icon}
                                        className={`text-xl ${
                                            benefit.color === 'blue' ? 'text-blue-600' :
                                            benefit.color === 'green' ? 'text-green-600' :
                                            benefit.color === 'purple' ? 'text-purple-600' :
                                            benefit.color === 'orange' ? 'text-orange-600' :
                                            benefit.color === 'pink' ? 'text-pink-600' :
                                            'text-indigo-600'
                                        }`}
                                    />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                                    {benefit.title}
                                </h3>
                                <p className="text-gray-600 mb-3">
                                    {benefit.description}
                                </p>
                                <div className="flex items-center text-sm text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span>Ver más</span>
                                    <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Secciones Principales */}
                <div className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                        Secciones de la Documentación
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Link
                            to="/documentation/quick-start"
                            className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all group"
                        >
                            <div className="flex items-center mb-3">
                                <FontAwesomeIcon icon={faRocket} className="text-blue-600 text-2xl mr-3" />
                                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">Quick Start</h3>
                            </div>
                            <p className="text-sm text-gray-600">Registro, login y configuración inicial</p>
                        </Link>

                        <Link
                            to="/documentation/gestion"
                            className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6 hover:shadow-lg transition-all group"
                        >
                            <div className="flex items-center mb-3">
                                <FontAwesomeIcon icon={faCog} className="text-green-600 text-2xl mr-3" />
                                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-green-600">Gestión</h3>
                            </div>
                            <p className="text-sm text-gray-600">Marcas, tiendas, clientes, productos y ventas</p>
                        </Link>

                        <Link
                            to="/documentation/marketing"
                            className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-6 hover:shadow-lg transition-all group"
                        >
                            <div className="flex items-center mb-3">
                                <FontAwesomeIcon icon={faBullhorn} className="text-purple-600 text-2xl mr-3" />
                                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-600">Marketing</h3>
                            </div>
                            <p className="text-sm text-gray-600">Ofertas, WinBack, recompensas, email e infografías</p>
                        </Link>

                        <Link
                            to="/documentation/analytics"
                            className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-xl p-6 hover:shadow-lg transition-all group"
                        >
                            <div className="flex items-center mb-3">
                                <FontAwesomeIcon icon={faChartLine} className="text-indigo-600 text-2xl mr-3" />
                                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600">Analytics</h3>
                            </div>
                            <p className="text-sm text-gray-600">Dashboards, estadísticas y reportes ejecutivos</p>
                        </Link>

                        <Link
                            to="/documentation/feedback"
                            className="bg-gradient-to-br from-pink-50 to-pink-100 border border-pink-200 rounded-xl p-6 hover:shadow-lg transition-all group"
                        >
                            <div className="flex items-center mb-3">
                                <FontAwesomeIcon icon={faPoll} className="text-pink-600 text-2xl mr-3" />
                                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-pink-600">Feedback</h3>
                            </div>
                            <p className="text-sm text-gray-600">Encuestas y centro de alertas</p>
                        </Link>

                        <Link
                            to="/documentation/usuarios"
                            className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-xl p-6 hover:shadow-lg transition-all group"
                        >
                            <div className="flex items-center mb-3">
                                <FontAwesomeIcon icon={faUser} className="text-teal-600 text-2xl mr-3" />
                                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-600">Usuarios</h3>
                            </div>
                            <p className="text-sm text-gray-600">Dueño, trabajador y franquiciado</p>
                        </Link>

                        <Link
                            to="/documentation/integraciones/tpv-agora"
                            className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6 hover:shadow-lg transition-all group"
                        >
                            <div className="flex items-center mb-3">
                                <FontAwesomeIcon icon={faQrcode} className="text-orange-600 text-2xl mr-3" />
                                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-orange-600">Integraciones</h3>
                            </div>
                            <p className="text-sm text-gray-600">TPV Agora y otras integraciones</p>
                        </Link>
                    </div>
                </div>

                {/* How Documentation Works */}
                <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                        <FontAwesomeIcon icon={faBook} className="text-blue-600" />
                        Cómo usar esta documentación
                    </h2>
                    <div className="space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                                1
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-1">
                                    Explora las guías disponibles
                                </h3>
                                <p className="text-gray-600">
                                    Navega por las diferentes secciones usando el menú lateral. Encontrarás guías completas sobre Quick Start, Gestión, Marketing, Analytics, Feedback, Tipos de Usuarios e Integraciones. Cada sección está organizada por categorías para facilitar la navegación.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                                2
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-1">
                                    Sigue los pasos indicados
                                </h3>
                                <p className="text-gray-600">
                                    Cada guía está diseñada paso a paso, con instrucciones claras y ejemplos prácticos para que puedas configurar todo sin problemas.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                                3
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-1">
                                    Contacta soporte si lo necesitas
                                </h3>
                                <p className="text-gray-600">
                                    Si tienes alguna duda o problema, nuestro equipo de soporte está disponible para ayudarte en cualquier momento.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Support Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                ¿Necesitas ayuda personalizada?
                            </h3>
                            <p className="text-gray-600">
                                Nuestro equipo de soporte está aquí para ayudarte con cualquier duda o problema que tengas.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3">
                            <a
                                href="mailto:soporte@petitprime.com"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <FontAwesomeIcon icon={faEnvelope} />
                                Contactar Soporte
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default DocumentationWelcome;

